import useRoutesHandler from "@/Hooks/useRouteHandler";

import { THookProps } from "@/types/global.types";
import { handleSuccessToast } from "@/UI/Toasts";
import { handleNetworkError } from "@/utility/networkError.utility";
import { useGetAuthUserService } from "@repo/api-services/admin";
import { CACHE_KEYS } from "@repo/api-services/CacheKeys";
import { handleGlobalPostRequest } from "@repo/api-services/changeRequestsGlobal.ts";
import { handleGlobalGetRequestQuery } from "@repo/api-services/getRequestsGlobal.ts";
import URLS from "@repo/api-services/URLS";
import { stringUtility } from "@repo/utilities/string.utility.ts";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  ICertificateActionPayload,
  IDownloadCertificateSubmitSchema,
  IRejectionSubmitSchema,
} from "../certificate.types";
import { removeEmptyFields } from "@repo/utilities/object.utility.ts";
import usePartners from "@/Pages/Partners/hooks/usePartners";
import { IPartnerResponseSchema } from "@/Pages/Partners/partners.types";
import { CERTIFICATE_BUTTONS } from "../certificate.constant";

interface ICertificateHookProps extends THookProps {
  enableCountQuery?: boolean;
  planId?: number | string;
  partnerId?: number | string;
}

export default function usePartnerCertificates({
  enablePagination = false,
  enablePerPage = false,
  enableQuery = true,
  enableCountQuery = false,
  partnerId = "",
  planId = "",
}: ICertificateHookProps) {
  // States
  const [errorFile, setErrorFile] = useState<string | null>(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedPartner, setSelectedPartner] =
    useState<IPartnerResponseSchema | null>(null);
  const [deletePayload, setDeletePayload] =
    useState<ICertificateActionPayload | null>(null);

  // Hooks
  const { urlQueries, navigate } = useRoutesHandler();

  const {
    services: { allPartnersService },
    states: { searchQuery: searchPartner, setSearchQuery: setSearchPartner },
  } = usePartners({});

  const [openModal, setOpenModal] = useState<
    | `${typeof CERTIFICATE_BUTTONS.download_certificate.code}`
    | `${typeof CERTIFICATE_BUTTONS.upload_cancel_certificate.code}`
    | `${typeof CERTIFICATE_BUTTONS.upload_delete_certificate.code}`
    | `${typeof CERTIFICATE_BUTTONS.download_loader_file.code}`
    | null
  >(null);
  const { authService } = useGetAuthUserService();
  const partnerLoginId = authService?.data?.data?.partner?.id;
  const page = enablePagination ? urlQueries.page || "1" : "1";
  const pageSize = enablePerPage ? urlQueries.pagesize || "15" : "15";
  const certificateId = urlQueries["certificate-id"] || "";
  const coiStatus = urlQueries?.["coi-status"] || "";
  const createdBetween = urlQueries?.["created_between"] || "";
  const certificate_no = urlQueries["certificate-no"] ?? "";
  const gross_premium = urlQueries["gross_premium"] ?? "";
  const customer_name = urlQueries["customer-name"] ?? "";
  const loan_account_number = urlQueries["loan_account_number"] ?? "";
  const currPartnerId = selectedPartner?.id ?? partnerId;
  const years = urlQueries["year"] ?? "2026";
  const filters = removeEmptyFields({
    "filter[certificate_stage_id]": coiStatus,
    "filter[certificate_no]": certificate_no,
    "filter[total_premium]": gross_premium,
    "filter[customer_name]": customer_name,
    "filter[loan_account_number]": loan_account_number,
    "filter[partner_id]": currPartnerId,
    "filter[plan_id]": planId,
  });

  // Services
  const getAllPartnerCertificatesService = useQuery({
    queryKey: [
      ...CACHE_KEYS.CERTIFICATES,
      filters,
      page,
      pageSize,
      createdBetween,
    ],
    queryFn: () =>
      handleGlobalGetRequestQuery({
        url: URLS.CERTIFICATES,
        searchParams: {
          ...filters,
          "filter[created_between]": createdBetween,
          page: page,
          per_page: pageSize,
          include:
            "certificate_stage,enquiry.proposer,partner,enquiry.subjects",
        },
      }),
    enabled: enableQuery,
  });
  // singlePartnerCertificate

  const getSinglePartnerCertificateService = useQuery({
    queryKey: [...CACHE_KEYS.SINGLE_CERTIFICATE, certificateId, page, pageSize],
    queryFn: () =>
      handleGlobalGetRequestQuery({
        url: `${URLS.CERTIFICATES}/${certificateId}`,
        searchParams: {
          include:
            "certificate_stage,enquiry.proposer,partner,certificatable.product.subSegment",
        },
      }),
    enabled: certificateId ? true : false,
  });

  const getAllCertificateStagesService = useQuery({
    queryKey: [...CACHE_KEYS.CERTIFICATES_STAGES, currPartnerId],
    queryFn: () =>
      handleGlobalGetRequestQuery({
        url: URLS.CERTIFICATES_STAGES,
        searchParams: {
          include: "certificates_count",
          "filter[partner_id]": currPartnerId,
        },
      }),
  });

  const getTotalPremiumCountService = useQuery({
    queryKey: [
      ...CACHE_KEYS.CERTIFICATES_PREMIUM_COUNT,
      years,
      planId,
      partnerId,
    ],
    queryFn: () =>
      handleGlobalGetRequestQuery({
        url: `/certificates/stats/total-premium`,
        searchParams: {
          by: "month",
          year: years,
          "filter[partner_id]": currPartnerId,
          "filter[plan_id]": planId,
        },
      }),
    enabled: enableCountQuery,
  });

  const postCancellationReasonMutation = useMutation({
    mutationFn: (data: ICertificateActionPayload) => {
      return handleGlobalPostRequest({
        url: `${URLS.CERTIFICATES}/${certificateId}/cancel`,
        data,
      });
    },
    onSuccess: () => {
      getAllPartnerCertificatesService?.refetch();
      getAllCertificateStagesService?.refetch();
      getSinglePartnerCertificateService?.refetch();
      handleSuccessToast({
        message: "Certificate cancelled successfully.",
      });
    },
    onError: ({ response }: { response: Error }) => {
      handleNetworkError({ response });
    },
  });

  const postDeleteReasonMutation = useMutation({
    mutationFn: (data: ICertificateActionPayload) => {
      return handleGlobalPostRequest({
        url: `${URLS.CERTIFICATES}/${certificateId}/delete`,
        data,
      });
    },
    onSuccess: () => {
      getAllPartnerCertificatesService?.refetch();
      getAllCertificateStagesService?.refetch();
      getSinglePartnerCertificateService?.refetch();
      setDeletePayload(null);
      handleSuccessToast({
        message: "Certificate delete successfully.",
      });
    },
    onError: ({ response }: { response: Error }) => {
      handleNetworkError(response);
    },
  });
  // function

  const handleConfirmDelete = () => {
    if (!deletePayload) return;
    postDeleteReasonMutation.mutate(deletePayload);
  };

  const handleCertificateAction = (data: IRejectionSubmitSchema) => {
    const dataObject = {
      cancellation_reason: data?.reason,
    };
    if (data?.action?.code === "delete") {
      setDeletePayload(dataObject);
      return;
    }
    postCancellationReasonMutation.mutate(dataObject);
  };

  const handleDownloadCertificate = (
    data: IDownloadCertificateSubmitSchema
  ) => {
    handleGlobalGetRequestQuery({
      url: `${URLS.CERTIFICATES}/download/${data?.type?.code}`,
      searchParams: {
        "filter[partner_id]": data?.partner?.id || "",
        "filter[certificatable_id]": data?.policy?.id || data?.product?.id,
        "filter[certificatable_type]": data?.certificate_type?.code,
        "filter[certificate_stage_id]": data?.certificate_stage?.id || "",
        from: `${stringUtility.removeCharAddSpace(fromDate, "T")}:00`,
        to: `${stringUtility.removeCharAddSpace(toDate, "T")}:00`,
      },
    }).then((response) => {
      const link = response?.data?.link;
      window.open(link, "_blank");
      setOpenModal(null);
      navigate("/proposal-management/certificates");
      handleSuccessToast({ message: "Certificate download successfully" });
    });
  };

  const handleModal = (val?: string | null) => {
    if (val) {
      setOpenModal(val);
      return;
    }
    setOpenModal(null);
  };
  return {
    services: {
      getAllPartnerCertificatesService,
      getAllCertificateStagesService,
      getTotalPremiumCountService,
      getSinglePartnerCertificateService,
      allPartnersService,
    },
    mutations: {
      postCancellationReasonMutation,
      postDeleteReasonMutation,
    },
    states: {
      errorFile,
      setErrorFile,
      partnerLoginId,
      openModal,
      setOpenModal,
      searchPartner,
      setSearchPartner,
      selectedPartner,
      setSelectedPartner,
      deletePayload,
      setDeletePayload,
    },
    function: {
      handleCertificateAction,
      handleDownloadCertificate,
      setFromDate,
      setToDate,
      handleConfirmDelete,
      handleModal,
    },
  };
}
